import { t } from "ttag";

import { usePatientQuery } from "@/app/hooks";
import { DURATION_IN_MONTHS } from "@/features/billing/models";
import type { Summary } from "@/features/products/models";
import { DISCOUNT_DURATION } from "@/shared/domains";
import type { Price } from "@/shared/domains";
import { PUERTO_RICO_STATE_VALUE } from "@/shared/models";
import { Alert, Heading } from "@/shared/ui";

interface PaymentDisclaimerProps {
  price: Price;
  summary: Summary;
}

export const PaymentDisclaimer = ({
  price,
  summary,
}: PaymentDisclaimerProps) => {
  const { discount } = price;

  const { data: isFromPuertoRico, isSuccess } = usePatientQuery(
    (patient) => patient.location?.state === PUERTO_RICO_STATE_VALUE,
  );

  if (!isSuccess) {
    return null;
  }

  const isQuarterlyShippingFrequency =
    isFromPuertoRico && price.durationInMonths === DURATION_IN_MONTHS.QUARTERLY;

  const shippingCadence = isQuarterlyShippingFrequency
    ? t`Three month plans shipped at once`
    : t`Medications ship monthly`;

  const hasInitialPaymentDifference =
    (discount?.isValid && discount.duration === DISCOUNT_DURATION.ONCE) ??
    summary.items.length > 0;

  return (
    <Alert variant="outlined" withIcon={false} className="text-sm">
      <Heading level={3} color="primary" size="xx-small">
        {t`By selecting “Pay”, you agree to the following:`}
      </Heading>

      <ul className="list-inside list-disc pl-2 text-neutral-800">
        {hasInitialPaymentDifference && (
          <li>{t`The coupon discount applies only to your first payment`}</li>
        )}
        <li>{t`Charges will appear as "OpenLoop Healthcare Partners PC" on your bank statement`}</li>
        <li>{t`No refunds or credits for partial plan periods are given unless required by law`}</li>
        <li>{t`Cancel anytime to stop future billing`}</li>
        <li>{t`If you pay for a treatment and your provider determines that you are ineligible, we will process a full refund`}</li>
        <li>
          {t`Plans offer discounts for longer commitments. ${shippingCadence}. Early cancellations are refunded at the standard monthly rate`}
        </li>
      </ul>
    </Alert>
  );
};
